import React, { useEffect, useState } from 'react';
import { filter, sumBy } from 'lodash';

import {
  getOrderItemName,
  getOrderItemPrice,
  isService,
  OLD_SERVICES_LIST_VIEW,
} from '@/components/modals/SubmitOrder/constants';
import { useOrderContext } from '@/components/modals/SubmitOrder/helpers';
import { OrderItemWrapper } from '@/components/modals/SubmitOrder/types';
import { formatPriceWithCurrency } from '@/utils/format-price';

import { OrderBlock } from '../../../Common/OrderBlock';
import { OrderBlockWrapper } from '../../../Common/OrderBlockWrapper';
import { getNonEquipmentItems, separateNonEquipmentItems } from '../../../ConfirmationStep/helpers';
import { SelectServices } from '../SelectServices';
import { isProductAvailable } from '../SelectServices/constants';

import {
  bundleItemsBySystemGroup,
  getBundleTotal,
  isDesignService,
  systemGroup,
} from './constants';
import { Props } from './types';

import styles from './ServiceDetails.module.scss';

export const ServiceDetails = ({ proposal }: Props) => {
  const state = useOrderContext();

  const isActivityDistributionModel = state.isDistribution;
  const nonEquipmentItems = getNonEquipmentItems(state);
  const proposalAdders = proposal?.adders.map(proposalAdder => proposalAdder.name) ?? [];
  const { services: distributionServices, servicesTotal: distributionServicesTotal } =
    separateNonEquipmentItems(state, nonEquipmentItems, proposalAdders);

  const services = isActivityDistributionModel
    ? distributionServices
    : filter(state.visibleOrderItems, oi => isService(oi));
  const { modifiedArray: servicesWithoutBasicServices, bundleServices: basicServicesBundle } =
    bundleItemsBySystemGroup(services, systemGroup['TS Basic Services']);
  const hasBasicServicesBundleItems = basicServicesBundle.length > 1;
  const basicServicesTotal = getBundleTotal(basicServicesBundle, state, proposal);
  const servicesToRender = hasBasicServicesBundleItems ? servicesWithoutBasicServices : services;
  const distributionServicesFilteredTotal = hasBasicServicesBundleItems
    ? sumBy(servicesWithoutBasicServices, service => getOrderItemPrice(state, service, proposal)) +
      basicServicesTotal
    : distributionServicesTotal;
  const totalToDisplay = isActivityDistributionModel
    ? distributionServicesFilteredTotal
    : sumBy(services, service => getOrderItemPrice(state, service, proposal));

  const showServicesSelector = !OLD_SERVICES_LIST_VIEW;
  const [visibleServices, setVisibleServices] = useState<OrderItemWrapper[]>([]);
  const [servicesTotal, setServicesTotal] = useState<number>(
    showServicesSelector ? 0 : totalToDisplay,
  );

  useEffect(() => {
    if (services && proposal && !showServicesSelector && !isActivityDistributionModel) {
      const bundledServicesTotal = hasBasicServicesBundleItems
        ? sumBy(servicesWithoutBasicServices, service =>
            getOrderItemPrice(state, service, proposal),
          ) + basicServicesTotal
        : sumBy(services, service => getOrderItemPrice(state, service, proposal));

      setServicesTotal(bundledServicesTotal);
    }
  }, [
    isActivityDistributionModel,
    proposal,
    services,
    showServicesSelector,
    hasBasicServicesBundleItems,
    servicesWithoutBasicServices,
  ]);

  const completeServices = services.filter(service => !isDesignService(service));
  const designServices = services.find(service => isDesignService(service));

  useEffect(() => {
    if (showServicesSelector && state.selectedServices) {
      if (state.selectedServices === 'Design Services') {
        const designServices = services.find(
          service => service.priceBookEntry.name === state.selectedServices,
        );

        if (designServices) {
          setVisibleServices([designServices]);
        }

        return;
      }

      const completeServices = services.filter(item => !isDesignService(item));

      setVisibleServices(completeServices);
    }
  }, [state.selectedServices]);

  useEffect(() => {
    if (showServicesSelector && visibleServices && !isActivityDistributionModel) {
      setServicesTotal(
        sumBy(visibleServices, service =>
          isProductAvailable(service) ? getOrderItemPrice(state, service, proposal) : 0,
        ),
      );
    }
  }, [visibleServices, proposal, isActivityDistributionModel]);

  useEffect(() => {}, [servicesTotal]);

  const isAnyServiceAvailable =
    completeServices.find(item => isProductAvailable(item)) ||
    (designServices && isProductAvailable(designServices));

  if (showServicesSelector && !isAnyServiceAvailable) {
    return null;
  }

  const renderBasicServicesBundle = hasBasicServicesBundleItems ? (
    <>
      <OrderBlockWrapper color="#FAFAFA">
        <div className={styles.service_wrapper}>
          <div className={styles.service_item}>Timberline Solar Basic Services</div>
          {!state.isPriceHidden && (
            <div className={styles.service_item}>{formatPriceWithCurrency(basicServicesTotal)}</div>
          )}
        </div>
      </OrderBlockWrapper>
      <br />
    </>
  ) : null;

  return (
    <>
      <OrderBlock hideValue={state.isPriceHidden} title="Services" value={servicesTotal}>
        <br />
        {showServicesSelector ? (
          <SelectServices
            completeServices={completeServices}
            designServices={designServices}
            selectedServices={state.selectedServices}
          />
        ) : (
          <>
            {servicesToRender.map((service: OrderItemWrapper, key: number) => (
              <div key={service.orderItem.productId + key}>
                <OrderBlockWrapper color="#FAFAFA">
                  <div className={styles.service_wrapper}>
                    <div className={styles.service_item}>{getOrderItemName(service)}</div>
                    {!state.isPriceHidden && (
                      <div className={styles.service_item}>
                        {formatPriceWithCurrency(getOrderItemPrice(state, service, proposal))}
                      </div>
                    )}
                  </div>
                </OrderBlockWrapper>
                <br />
              </div>
            ))}

            {renderBasicServicesBundle}
          </>
        )}
      </OrderBlock>
      <br />
    </>
  );
};
